import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  CSSProperties,
} from "react";
import { useAppContext } from "../components/AppContext";
import {
  darkModeBorder,
  darkModeWidgetBackground,
  darkModeWidgetBorderColor,
  lightModeBorder,
  lightModeWidgetBackground,
  lightModeWidgetBorderColor,
  negativeRed,
  positiveGreen,
} from "../util/style";

interface SortState {
  column: string;
  direction: "asc" | "desc";
}

interface SortIndicatorProps {
  column: string;
  sortState: SortState;
}

interface AccountRow {
  account: string;
  name: string;
  unrealized: number;
  realized: number;
  overall: number;
  marketValue: number;
}

interface TickerRow {
  ticker: string;
  shares: number;
  price: number;
  dayGain: number;
  marketValue: number;
  unrealized: number;
  realized: number;
  overall: number;
  overallPerc: number;
}

const Summary: React.FC = () => {
  const {
    allAccountString,
    getFont,
    darkMode,
    displayDataParsed,
    currencyMultiplier,
    numFormat,
    accountInfo,
    lastPrice,
    prevClosePrice,
  } = useAppContext() || {};

  const [isMobile, setIsMobile] = useState<boolean>(false);
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const prevDataRef = useRef<
    Record<
      string,
      {
        price?: number;
        prevClose?: number;
        dayGain?: number;
        marketValue?: number;
      }
    >
  >({});
  const [highlighted, setHighlighted] = useState<Record<string, boolean>>({});

  const [accountSort, setAccountSort] = useState<SortState>({
    column: "marketValue",
    direction: "desc",
  });
  const [holdingsSort, setHoldingsSort] = useState<SortState>({
    column: "marketValue",
    direction: "desc",
  });
  const [tickerSort, setTickerSort] = useState<SortState>({
    column: "marketValue",
    direction: "desc",
  });

  const [showAccount, setShowAccount] = useState<boolean>(true);
  const [showHoldings, setShowHoldings] = useState<boolean>(true);
  const [showTicker, setShowTicker] = useState<boolean>(true);

  const tableStyles: {
    table: CSSProperties;
    cell: CSSProperties;
    rightAlignedCell: CSSProperties;
    headerCell: CSSProperties;
  } = {
    table: {
      borderCollapse: "collapse",
      border: `1px solid ${
        darkMode ? darkModeWidgetBorderColor : lightModeWidgetBorderColor
      }`,
      fontSize: getFont?.(isMobile ? 8 : 12),
      width: "100%",
      background: darkMode
        ? darkModeWidgetBackground
        : lightModeWidgetBackground,
    },
    cell: {
      border: `1px solid ${darkMode ? darkModeBorder : lightModeBorder}`,
      padding: "1px 2px",
    },
    rightAlignedCell: {
      border: `1px solid ${darkMode ? darkModeBorder : lightModeBorder}`,
      padding: "1px 2px",
      textAlign: "right" as const,
    },
    headerCell: {
      border: `1px solid ${darkMode ? darkModeBorder : lightModeBorder}`,
      padding: "1px 2px",
      fontWeight: "bold",
      textAlign: "left" as const,
      cursor: "pointer",
      userSelect: "none" as const,
    },
  };

  const allAccountData = displayDataParsed?.accounts?.[allAccountString || ""];
  const accountKeys = Object.keys(displayDataParsed?.accounts || {}).filter(
    (key) => key !== allAccountString,
  );

  const totalMarketValue = useMemo(() => {
    if (!allAccountData?.currentHoldings) return 0;
    return Object.entries(allAccountData.currentHoldings).reduce(
      (acc, [ticker, shares]) => {
        const price = lastPrice?.[ticker] || 0;
        return acc + Math.abs(shares * price);
      },
      0,
    );
  }, [allAccountData?.currentHoldings, lastPrice]);

  const totalDayGain = useMemo(() => {
    if (!allAccountData?.currentHoldings) return 0;
    return Object.entries(allAccountData.currentHoldings).reduce(
      (acc, [ticker, shares]) => {
        const price = lastPrice?.[ticker] || 0;
        const prevClose = prevClosePrice?.[ticker] || price;
        const dayChange = (price - prevClose) * shares;
        return acc + dayChange;
      },
      0,
    );
  }, [allAccountData?.currentHoldings, lastPrice, prevClosePrice]);

  useEffect(() => {
    const changed: Record<string, boolean> = {};
    const allTickers = Object.keys(allAccountData?.trades || {});

    allTickers.forEach((ticker) => {
      const prev = prevDataRef.current[ticker] || {};
      const curr = {
        price: lastPrice?.[ticker],
        prevClose: prevClosePrice?.[ticker],
      };

      if (prev.price !== curr.price || prev.prevClose !== curr.prevClose) {
        changed[ticker] = true;
        setTimeout(() => {
          setHighlighted((prev) => ({ ...prev, [ticker]: false }));
        }, 1000);
      }

      prevDataRef.current[ticker] = curr;
    });

    const prevTotals = prevDataRef.current._totals || {};
    const currTotals = {
      dayGain: totalDayGain,
      marketValue: totalMarketValue,
    };
    if (
      prevTotals.dayGain !== currTotals.dayGain ||
      prevTotals.marketValue !== currTotals.marketValue
    ) {
      changed._totals = true;
      setTimeout(() => {
        setHighlighted((prev) => ({ ...prev, _totals: false }));
      }, 1000);
    }
    prevDataRef.current._totals = currTotals;

    if (Object.keys(changed).length > 0) {
      setHighlighted((prev) => ({ ...prev, ...changed }));
    }
  }, [
    lastPrice,
    prevClosePrice,
    allAccountData?.trades,
    totalDayGain,
    totalMarketValue,
  ]);

  const getAccountMarketValue = (account: string): number => {
    const holdings =
      displayDataParsed?.accounts?.[account]?.currentHoldings || {};
    return Object.entries(holdings).reduce((acc, [ticker, shares]) => {
      const price = lastPrice?.[ticker] || 0;
      return acc + Math.abs(shares * price);
    }, 0);
  };

  const getColor = (value: number, option: string): string => {
    if (option === "none" || (value > -0.0001 && value < 0.0001)) {
      return "inherit";
    } else if (value < -0.0001) {
      return negativeRed;
    } else if (option === "pl") {
      return positiveGreen;
    } else return "inherit";
  };

  const handleSort = (section: string, column: string): void => {
    const setSort =
      section === "account"
        ? setAccountSort
        : section === "holdings"
          ? setHoldingsSort
          : setTickerSort;
    const currentSort =
      section === "account"
        ? accountSort
        : section === "holdings"
          ? holdingsSort
          : tickerSort;

    if (currentSort.column === column) {
      setSort({
        column,
        direction: currentSort.direction === "desc" ? "asc" : "desc",
      });
    } else {
      setSort({ column, direction: "desc" });
    }
  };

  const SortIndicator: React.FC<SortIndicatorProps> = ({
    column,
    sortState,
  }) => {
    if (sortState.column !== column) return null;
    return (
      <span style={{ marginLeft: "2px", fontSize: "8px" }}>
        {sortState.direction === "desc" ? "▼" : "▲"}
      </span>
    );
  };

  const accountInfoTyped = accountInfo as
    | Record<string, { name?: string; order?: number }>
    | undefined;

  const sortedAccounts = useMemo((): AccountRow[] => {
    const accounts: AccountRow[] = accountKeys.map((account) => ({
      account,
      name: accountInfoTyped?.[account]?.name || account,
      unrealized: displayDataParsed?.accounts?.[account]?.unrealizedPnL || 0,
      realized: displayDataParsed?.accounts?.[account]?.realizedPnL || 0,
      overall: displayDataParsed?.accounts?.[account]?.overallPnL || 0,
      marketValue: getAccountMarketValue(account),
    }));

    if (!accountSort.column) return accounts;

    return [...accounts].sort((a, b) => {
      const mult = accountSort.direction === "desc" ? -1 : 1;
      if (accountSort.column === "name")
        return mult * a.name.localeCompare(b.name);
      return (
        mult *
        ((a[accountSort.column as keyof AccountRow] as number) -
          (b[accountSort.column as keyof AccountRow] as number))
      );
    });
  }, [
    accountKeys,
    accountInfoTyped,
    displayDataParsed,
    accountSort,
    lastPrice,
  ]);

  const sortedHoldings = useMemo((): TickerRow[] => {
    const holdings: TickerRow[] = Object.entries(allAccountData?.trades || {})
      .filter(
        ([, tickerData]) =>
          Math.abs(
            (tickerData as { resultingShareCount?: number })
              ?.resultingShareCount || 0,
          ) > 0.0001,
      )
      .map(([ticker, tickerData]) => {
        const data = tickerData as {
          resultingShareCount?: number;
          unrealizedPnL?: number;
          realizedPnL?: number;
          overallPnL?: number;
        };
        const price = lastPrice?.[ticker] || 0;
        const prevClose = prevClosePrice?.[ticker] || price;
        const dayGain = (price - prevClose) * (data?.resultingShareCount || 0);
        const marketValue = Math.abs((data?.resultingShareCount || 0) * price);
        const unrealized = data?.unrealizedPnL || 0;
        const overall = data?.overallPnL || 0;
        const costBasis = marketValue - unrealized;
        const overallPerc = costBasis !== 0 ? (overall / Math.abs(costBasis)) * 100 : 0;
        return {
          ticker,
          shares: data?.resultingShareCount || 0,
          price,
          dayGain,
          marketValue,
          unrealized,
          realized: data?.realizedPnL || 0,
          overall,
          overallPerc,
        };
      });

    return [...holdings].sort((a, b) => {
      const mult = holdingsSort.direction === "desc" ? -1 : 1;
      if (holdingsSort.column === "ticker")
        return mult * a.ticker.localeCompare(b.ticker);
      return (
        mult *
        ((a[holdingsSort.column as keyof TickerRow] as number) -
          (b[holdingsSort.column as keyof TickerRow] as number))
      );
    });
  }, [allAccountData?.trades, lastPrice, prevClosePrice, holdingsSort]);

  const sortedTickers = useMemo((): TickerRow[] => {
    const tickers: TickerRow[] = Object.entries(
      allAccountData?.trades || {},
    ).map(([ticker, tickerData]) => {
      const data = tickerData as {
        resultingShareCount?: number;
        unrealizedPnL?: number;
        realizedPnL?: number;
        overallPnL?: number;
      };
      const price = lastPrice?.[ticker] || 0;
      const prevClose = prevClosePrice?.[ticker] || price;
      const dayGain = (price - prevClose) * (data?.resultingShareCount || 0);
      const marketValue = Math.abs((data?.resultingShareCount || 0) * price);
      const unrealized = data?.unrealizedPnL || 0;
      const overall = data?.overallPnL || 0;
      const costBasis = marketValue - unrealized;
      const overallPerc = costBasis !== 0 ? (overall / Math.abs(costBasis)) * 100 : 0;
      return {
        ticker,
        shares: data?.resultingShareCount || 0,
        price,
        dayGain,
        marketValue,
        unrealized,
        realized: data?.realizedPnL || 0,
        overall,
        overallPerc,
      };
    });

    return [...tickers].sort((a, b) => {
      const mult = tickerSort.direction === "desc" ? -1 : 1;
      if (tickerSort.column === "ticker")
        return mult * a.ticker.localeCompare(b.ticker);
      return (
        mult *
        ((a[tickerSort.column as keyof TickerRow] as number) -
          (b[tickerSort.column as keyof TickerRow] as number))
      );
    });
  }, [allAccountData?.trades, lastPrice, prevClosePrice, tickerSort]);

  return (
    <div
      style={{
        height: "calc(100% - 0px)",
        padding: "10px 10px 10px 10px",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: "100%",
          width: "100%",
          borderRadius: "2px",
          overflow: "auto",
          background: darkMode
            ? darkModeWidgetBackground
            : lightModeWidgetBackground,
          padding: "6px",
          border: `1px solid ${
            darkMode ? darkModeWidgetBorderColor : lightModeWidgetBorderColor
          }`,
        }}
        className="hoverShadow"
      >
        {/* Overall Summary */}
        <div style={{ marginBottom: "8px" }}>
          <h3 style={{ margin: "0 0 4px 0", fontSize: getFont?.(14) }}>
            All Account Summary
          </h3>
          <table style={tableStyles.table}>
            <tbody>
              <tr>
                <td style={tableStyles.cell}>Day Gain</td>
                <td
                  style={{
                    ...tableStyles.rightAlignedCell,
                    color: getColor(totalDayGain, "pl"),
                  }}
                >
                  <span className={highlighted._totals ? "flash" : ""}>
                    $
                    {numFormat?.(
                      totalDayGain * (currencyMultiplier || 1),
                      true,
                    )}
                  </span>
                </td>
              </tr>
              <tr>
                <td style={tableStyles.cell}>Market Value</td>
                <td style={tableStyles.rightAlignedCell}>
                  $
                  {numFormat?.(
                    totalMarketValue * (currencyMultiplier || 1),
                    true,
                  )}
                </td>
              </tr>
              <tr>
                <td style={tableStyles.cell}>Unrealized P&L</td>
                <td style={tableStyles.rightAlignedCell}>
                  $
                  {numFormat?.(
                    (allAccountData?.unrealizedPnL || 0) *
                      (currencyMultiplier || 1),
                    true,
                  )}
                </td>
              </tr>
              <tr>
                <td style={tableStyles.cell}>Realized P&L</td>
                <td style={tableStyles.rightAlignedCell}>
                  $
                  {numFormat?.(
                    (allAccountData?.realizedPnL || 0) *
                      (currencyMultiplier || 1),
                    true,
                  )}
                </td>
              </tr>
              <tr>
                <td style={{ ...tableStyles.cell, fontWeight: "bold" }}>
                  Overall P&L
                </td>
                <td
                  style={{
                    ...tableStyles.rightAlignedCell,
                    fontWeight: "bold",
                  }}
                >
                  $
                  {numFormat?.(
                    (allAccountData?.overallPnL || 0) *
                      (currencyMultiplier || 1),
                    true,
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Per Account Summary */}
        <div style={{ marginBottom: "8px" }}>
          <h3
            style={{
              margin: "0 0 4px 0",
              fontSize: getFont?.(14),
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => setShowAccount(!showAccount)}
          >
            {showAccount ? "-" : "+"} By Account
          </h3>
          {showAccount && (
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th
                    style={tableStyles.headerCell}
                    onClick={() => handleSort("account", "name")}
                  >
                    Account
                    <SortIndicator column="name" sortState={accountSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("account", "marketValue")}
                  >
                    Mkt Value
                    <SortIndicator
                      column="marketValue"
                      sortState={accountSort}
                    />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("account", "unrealized")}
                  >
                    Unrealized
                    <SortIndicator
                      column="unrealized"
                      sortState={accountSort}
                    />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("account", "realized")}
                  >
                    Realized
                    <SortIndicator column="realized" sortState={accountSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("account", "overall")}
                  >
                    Overall P&L
                    <SortIndicator column="overall" sortState={accountSort} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedAccounts.map((row) => {
                  const hasHoldings = row.marketValue > 0;
                  const rowOpacity = hasHoldings ? 1 : 0.75;
                  return (
                    <tr key={row.account} style={{ opacity: rowOpacity }}>
                      <td style={tableStyles.cell}>{row.name}</td>
                      <td style={tableStyles.rightAlignedCell}>
                        $
                        {numFormat?.(
                          row.marketValue * (currencyMultiplier || 1),
                          true,
                        )}
                      </td>
                      <td style={tableStyles.rightAlignedCell}>
                        $
                        {numFormat?.(
                          row.unrealized * (currencyMultiplier || 1),
                          true,
                        )}
                      </td>
                      <td style={tableStyles.rightAlignedCell}>
                        $
                        {numFormat?.(
                          row.realized * (currencyMultiplier || 1),
                          true,
                        )}
                      </td>
                      <td
                        style={{
                          ...tableStyles.rightAlignedCell,
                          fontWeight: "bold",
                          color: getColor(row.overall, "pl"),
                        }}
                      >
                        $
                        {numFormat?.(
                          row.overall * (currencyMultiplier || 1),
                          true,
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Per Ticker Summary */}
        <div>
          <h3
            style={{
              margin: "0 0 4px 0",
              fontSize: getFont?.(14),
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => setShowTicker(!showTicker)}
          >
            {showTicker ? "-" : "+"} By Ticker (All Accounts)
          </h3>
          {showTicker && (
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th
                    style={tableStyles.headerCell}
                    onClick={() => handleSort("ticker", "ticker")}
                  >
                    Ticker
                    <SortIndicator column="ticker" sortState={tickerSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("ticker", "shares")}
                  >
                    Shares
                    <SortIndicator column="shares" sortState={tickerSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("ticker", "price")}
                  >
                    Price
                    <SortIndicator column="price" sortState={tickerSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("ticker", "dayPerc")}
                  >
                    Day %
                    <SortIndicator column="dayPerc" sortState={tickerSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("ticker", "dayGain")}
                  >
                    Day Gain
                    <SortIndicator column="dayGain" sortState={tickerSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("ticker", "marketValue")}
                  >
                    Mkt Value
                    <SortIndicator
                      column="marketValue"
                      sortState={tickerSort}
                    />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("ticker", "unrealized")}
                  >
                    Unrealized
                    <SortIndicator column="unrealized" sortState={tickerSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("ticker", "realized")}
                  >
                    Realized
                    <SortIndicator column="realized" sortState={tickerSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("ticker", "overallPerc")}
                  >
                    Overall %
                    <SortIndicator column="overallPerc" sortState={tickerSort} />
                  </th>
                  <th
                    style={{ ...tableStyles.headerCell, textAlign: "right" }}
                    onClick={() => handleSort("ticker", "overall")}
                  >
                    Overall P&L
                    <SortIndicator column="overall" sortState={tickerSort} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedTickers.map((row) => (
                  <tr key={row.ticker}>
                    <td style={tableStyles.cell}>{row.ticker}</td>
                    <td style={tableStyles.rightAlignedCell}>
                      {numFormat?.(row.shares, false, 0)}
                    </td>
                    <td style={tableStyles.rightAlignedCell}>
                      <span className={highlighted[row.ticker] ? "flash" : ""}>
                        ${numFormat?.(row.price, false, 2)}
                      </span>
                    </td>
                    <td
                      style={{
                        ...tableStyles.rightAlignedCell,
                        color: getColor(row.dayGain, "pl"),
                      }}
                    >
                      <span className={highlighted[row.ticker] ? "flash" : ""}>
                        {numFormat?.(
                          (row.dayGain / (row.marketValue - row.dayGain)) * 100,
                          true,
                        )}
                        %
                      </span>
                    </td>
                    <td
                      style={{
                        ...tableStyles.rightAlignedCell,
                        color: getColor(row.dayGain, "pl"),
                      }}
                    >
                      <span className={highlighted[row.ticker] ? "flash" : ""}>
                        $
                        {numFormat?.(
                          row.dayGain * (currencyMultiplier || 1),
                          true,
                        )}
                      </span>
                    </td>
                    <td style={tableStyles.rightAlignedCell}>
                      <span className={highlighted[row.ticker] ? "flash" : ""}>
                        $
                        {numFormat?.(
                          row.marketValue * (currencyMultiplier || 1),
                          true,
                        )}
                      </span>
                    </td>
                    <td style={tableStyles.rightAlignedCell}>
                      $
                      {numFormat?.(
                        row.unrealized * (currencyMultiplier || 1),
                        true,
                      )}
                    </td>
                    <td style={tableStyles.rightAlignedCell}>
                      $
                      {numFormat?.(
                        row.realized * (currencyMultiplier || 1),
                        true,
                      )}
                    </td>
                    <td
                      style={{
                        ...tableStyles.rightAlignedCell,
                        fontWeight: "bold",
                        color: getColor(row.overallPerc, "pl"),
                      }}
                    >
                      {numFormat?.(row.overallPerc, true)}%
                    </td>
                    <td
                      style={{
                        ...tableStyles.rightAlignedCell,
                        fontWeight: "bold",
                        color: getColor(row.overall, "pl"),
                      }}
                    >
                      $
                      {numFormat?.(
                        row.overall * (currencyMultiplier || 1),
                        true,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Summary;
